import React, { useMemo, useState } from 'react';
import { rateColumns, goalieRateColumns } from '../constants/ratings';
import PlayerNameBadges from './PlayerNameBadges';

const GOALIE_RATE_NAMES = goalieRateColumns.map((c) => c.name);

function DiffStrip({ playerA, playerB, isGoalie = false }) {
  const cols = isGoalie ? GOALIE_RATE_NAMES : rateColumns;

  return (
    <div className="wishlist-ratings-scroll">
      <div
        className={`player-ratings wishlist-ratings wishlist-ratings-diff ${
          isGoalie ? 'goalie-ratings' : 'skater-ratings'
        }`}
      >
        {cols.map((name) => {
          const aVal = playerA.rates?.[name] ?? playerA[name] ?? 0;
          const bVal = playerB.rates?.[name] ?? playerB[name] ?? 0;
          const diff = aVal - bVal;
          const cls = diff > 0 ? 'positive' : diff < 0 ? 'negative' : 'neutral';
          return (
            <div key={name} className={`rating-item wishlist-diff-chip ${cls}`}>
              <span className="rating-label">{name}</span>
              <span className="rating-value">
                {diff === 0 ? '0' : `${diff > 0 ? '+' : ''}${diff}`}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PlayerPick({ label, value, options, onChange }) {
  return (
    <div className="form-group mb-0 compare-modal-pick">
      <label className="form-label">{label}</label>
      <select className="form-select" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Select player...</option>
        {options.map((p) => (
          <option key={p.id} value={String(p.id)}>
            {p.name} {p.positionDisplay ? `(${p.positionDisplay})` : ''} · OV {p.OV || '—'}
          </option>
        ))}
      </select>
    </div>
  );
}

export default function PlayerCompareModal({
  open,
  onClose,
  players,
  goalies,
  initialPlayerA = null,
  initialPlayerB = null,
}) {
  const [idA, setIdA] = useState(initialPlayerA ? String(initialPlayerA.id) : '');
  const [idB, setIdB] = useState(initialPlayerB ? String(initialPlayerB.id) : '');
  const [pool, setPool] = useState(
    initialPlayerA && goalies.some((g) => g.id === initialPlayerA.id) ? 'goalies' : 'skaters'
  );

  const options = useMemo(() => {
    const list = pool === 'goalies' ? goalies : players;
    return [...list].sort((a, b) => (b.OV || 0) - (a.OV || 0));
  }, [pool, players, goalies]);

  const playerA = options.find((p) => String(p.id) === idA) || null;
  const playerB = options.find((p) => String(p.id) === idB) || null;
  const isGoalie = pool === 'goalies';

  if (!open) return null;

  return (
    <div className="compare-modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="compare-modal card"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Compare players"
      >
        <div className="card-header compare-modal-header">
          <h2 className="card-title">Compare players</h2>
          <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>
            Close
          </button>
        </div>
        <div className="card-content compare-modal-body">
          <div className="compare-modal-toolbar">
            <div className="form-group mb-0">
              <label className="form-label">Pool</label>
              <select
                className="form-select"
                value={pool}
                onChange={(e) => {
                  setPool(e.target.value);
                  setIdA('');
                  setIdB('');
                }}
              >
                <option value="skaters">Skaters</option>
                <option value="goalies">Goalies</option>
              </select>
            </div>
            <PlayerPick label="Player A" value={idA} options={options} onChange={setIdA} />
            <PlayerPick label="Player B" value={idB} options={options} onChange={setIdB} />
          </div>

          {playerA && playerB ? (
            <div className="compare-modal-result">
              <div className="compare-modal-names">
                <div>
                  <strong className="player-name-row player-name-row-inline">
                    <span className="player-name-text">{playerA.name}</span>
                    <PlayerNameBadges player={playerA} size="small" showPlayerType={!isGoalie} />
                  </strong>
                  <div className="wishlist-card-meta">
                    <span>{isGoalie ? 'Goalie' : playerA.positionDisplay}</span>
                    <span>OV {playerA.OV || '—'}</span>
                  </div>
                </div>
                <span className="compare-modal-vs">vs</span>
                <div>
                  <strong className="player-name-row player-name-row-inline">
                    <span className="player-name-text">{playerB.name}</span>
                    <PlayerNameBadges player={playerB} size="small" showPlayerType={!isGoalie} />
                  </strong>
                  <div className="wishlist-card-meta">
                    <span>{isGoalie ? 'Goalie' : playerB.positionDisplay}</span>
                    <span>OV {playerB.OV || '—'}</span>
                  </div>
                </div>
              </div>
              <p className="compare-modal-hint">Difference = A − B</p>
              <DiffStrip playerA={playerA} playerB={playerB} isGoalie={isGoalie} />
            </div>
          ) : (
            <p className="compare-modal-empty">Pick two players to see the rating difference.</p>
          )}
        </div>
      </div>
    </div>
  );
}
